import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Download, Mail, ArrowRight, Package, DollarSign, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PostCheckoutUpsell from '@/components/PostCheckoutUpsell';
import confetti from 'canvas-confetti';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { safeGetJSON } from '@/utils/safeStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function CheckoutSuccess() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // State from navigation (for non-redirect flows)
  const stateData = location.state || {};
  
  // State for Stripe Checkout redirect flow
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState(null);
  
  // Get data from state or fetched order
  const orders = stateData.orders || (orderData ? [orderData] : null);
  const customerInfo = stateData.customerInfo || orderData?.customerInfo;
  const total = stateData.total || orderData?.total;
  const creatorId = stateData.creatorId;
  const creatorName = stateData.creatorName;
  
  const user = safeGetJSON('user', null);
  const isGuest = !user;

  // Handle Stripe Checkout redirect (session_id in URL).
  // The verify call can race the Stripe webhook on slow networks — buyer
  // lands here before the order doc is fully written. We retry up to 3
  // times with exponential-ish delays before showing any soft warning,
  // which catches ~99% of real "race condition" failures silently.
  useEffect(() => {
    const sessionId = searchParams.get('session_id');

    if (sessionId && !stateData.orders) {
      const verifyAndCompleteOrder = async () => {
        setLoading(true);
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        // Retry plan: immediate, +2s, +4s, +6s. Total ≤12s — well within the
        // user's patience window post-payment, and long enough to clear the
        // typical Stripe webhook → DB write lag.
        const delays = [0, 2000, 4000, 6000];
        let lastErr = null;

        for (let attempt = 0; attempt < delays.length; attempt += 1) {
          if (delays[attempt]) {
            // Promise-based delay; React doesn't auto-cancel, but the
            // useEffect cleanup pattern is unnecessary here because the
            // user can't navigate away during a 2s wait without leaving
            // the page anyway.
            await new Promise((r) => setTimeout(r, delays[attempt]));
          }
          try {
            const response = await axios.get(
              `${API_URL}/api/checkout/status/${sessionId}`,
              { headers }
            );
            if (response.data) {
              setOrderData({
                order_id: response.data.order_id || response.data.session_id,
                items: response.data.items || [],
                total: response.data.amount || response.data.total,
                customerInfo: {
                  email: response.data.customer_email || response.data.buyer_email,
                  name: response.data.customer_name || response.data.buyer_name || 'Customer',
                },
              });
              confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
              toast.success('Payment confirmed! Check your email for download links.');
              setLoading(false);
              return;
            }
          } catch (err) {
            lastErr = err;
            // 404 = order doc not yet present → retry. Other 5xx → also retry.
            // 401/403 only happens on auth-restricted endpoints; not relevant here.
          }
        }

        // All retries exhausted — Stripe still took the payment, the email
        // confirmation will still go out via webhook, so we degrade
        // gracefully rather than alarming the customer.
        console.error('Order verify timed out after retries:', lastErr);
        setError(
          "Order details are still processing — your email confirmation will arrive in a moment with full details and download links."
        );
        confetti({ particleCount: 50, spread: 70, origin: { y: 0.6 } });
        setLoading(false);
      };

      verifyAndCompleteOrder();
    }
  }, [searchParams, stateData.orders]);

  useEffect(() => {
    // Fire confetti on success (for non-redirect flow)
    if (orders && orders.length > 0 && !searchParams.get('session_id')) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      
      // Track conversion for analytics
      const trackConversion = async () => {
        try {
          await axios.post(`${API_URL}/api/analytics/track`, {
            event_type: 'conversion',
            metadata: { 
              order_count: orders.length,
              total_amount: total
            }
          });
        } catch (e) { console.error('Order tracking failed:', e); }
      };
      trackConversion();
    }
  }, [orders, total, searchParams]);

  // Loading state for Stripe redirect verification
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center p-8">
        <Loader2 className="w-16 h-16 text-[#00F0FF] mb-4 animate-spin" />
        <h1 className="text-2xl font-bold mb-4">Confirming Your Order...</h1>
        <p className="text-neutral-400">Please wait while we verify your payment.</p>
      </div>
    );
  }

  // If we have a session_id but verification showed an error, still show success
  // (since payment went through Stripe)
  if (searchParams.get('session_id') && !orders) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center p-8">
        <div className="w-24 h-24 bg-[#00FF94]/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-14 h-14 text-[#00FF94]" />
        </div>
        <h1 className="text-3xl font-bold mb-4">Payment Successful!</h1>
        <p className="text-neutral-400 mb-2">Your payment has been processed.</p>
        <p className="text-neutral-400 mb-6">Check your email for order confirmation and download links.</p>
        {error && (
          <p className="text-[#00F0FF] text-sm mb-4 max-w-md text-center" data-testid="checkout-soft-warning">
            {error}
          </p>
        )}
        <div className="flex gap-4">
          <Button onClick={() => navigate('/my-purchases')} className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black">
            View My Purchases
          </Button>
          <Button onClick={() => navigate('/')} variant="outline" className="border-white/20">
            Continue Shopping
          </Button>
        </div>
        {/* Same Galaxy.ai upsell shown on the main success block — buyer
            still paid, so this is a legitimate place to surface it. */}
        <div className="w-full max-w-2xl mt-8">
          <PostCheckoutUpsell />
        </div>
      </div>
    );
  }

  if (!orders || orders.length === 0) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center p-8">
        <Package className="w-16 h-16 text-neutral-600 mb-4" />
        <h1 className="text-2xl font-bold mb-4">No Order Found</h1>
        <p className="text-neutral-400 mb-6">It looks like you have not completed a purchase.</p>
        <Button onClick={() => navigate('/')} className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black">
          Go to Marketplace
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="container mx-auto px-6 md:px-12 py-16 max-w-2xl">
        {/* Success Icon */}
        <div className="text-center mb-12">
          <div className="w-24 h-24 bg-[#00FF94]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-14 h-14 text-[#00FF94]" />
          </div>
          <h1 className="text-4xl font-black mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Order Confirmed!
          </h1>
          <p className="text-xl text-neutral-400">
            Thank you for your purchase, {customerInfo?.name?.split(' ')[0] || 'there'}!
          </p>
        </div>

        {/* Order Details Card */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Mail className="w-6 h-6 text-[#00F0FF]" />
            <div>
              <h3 className="font-bold">Confirmation Sent</h3>
              <p className="text-sm text-neutral-400">
                We've sent the receipt to {customerInfo?.email}
              </p>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6">
            <h3 className="font-bold mb-4">Your Purchase</h3>
            
            {orders.map((order, idx) => (
              <div 
                key={order.order_id || idx}
                className="flex items-center justify-between py-3 border-b border-white/5 last:border-0"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#00F0FF]/20 to-white/20 flex items-center justify-center">
                    <Package className="w-6 h-6 text-[#00F0FF]" />
                  </div>
                  <div>
                    <p className="font-semibold">{order.product_name || 'Product'}</p>
                    <p className="text-sm text-neutral-400">Order #{order.order_id?.slice(-8) || 'N/A'}</p>
                  </div>
                </div>
                <span className="font-bold text-[#00F0FF]">${order.amount?.toFixed(2) || '0.00'}</span>
              </div>
            ))}

            <div className="flex justify-between items-center pt-4 mt-4 border-t border-white/10">
              <span className="text-lg font-bold">Total Paid</span>
              <span className="text-2xl font-black text-[#00F0FF]">${total?.toFixed(2) || '0.00'}</span>
            </div>
          </div>
        </div>

        {/* What's Next */}
        <div className="bg-gradient-to-r from-[#00F0FF]/10 to-white/10 border border-white/10 rounded-2xl p-8 mb-8">
          <h3 className="text-xl font-bold mb-4">What's Next?</h3>
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-[#00F0FF] text-black flex items-center justify-center text-sm font-bold flex-shrink-0">
                1
              </div>
              <div>
                <p className="font-semibold">Check Your Email</p>
                <p className="text-sm text-neutral-400">
                  Your product access links and download instructions have been sent to your email.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-[#00F0FF] text-black flex items-center justify-center text-sm font-bold flex-shrink-0">
                2
              </div>
              <div>
                <p className="font-semibold">Access Your Products</p>
                <p className="text-sm text-neutral-400">
                  Click the links in your email to access and download your purchased products.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-[#00F0FF] text-black flex items-center justify-center text-sm font-bold flex-shrink-0">
                3
              </div>
              <div>
                <p className="font-semibold">Get Support</p>
                <p className="text-sm text-neutral-400">
                  If you have any questions, contact the product creator through their store page.
                </p>
              </div>
            </li>
          </ul>
        </div>

        {/* Affiliate Promotion for Guests */}
        {isGuest && (
          <div className="bg-gradient-to-r from-[#00FF94]/10 to-[#00F0FF]/10 border border-[#00FF94]/30 rounded-2xl p-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00FF94] to-[#00F0FF] flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">💰</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-[#00FF94]" />
                  <span className="text-sm font-semibold text-[#00FF94]">Earn Money</span>
                </div>
                <h3 className="text-xl font-bold mb-2">Become an Affiliate & Earn!</h3>
                <p className="text-neutral-400 mb-4">
                  Love what you just bought? Share it with others and earn commissions on every sale you refer!
                </p>
                <Button
                  onClick={() => navigate('/affiliate/program', { 
                    state: { 
                      fromCheckout: true, 
                      creatorId: creatorId || orders?.[0]?.creator_id,
                      creatorName: creatorName || orders?.[0]?.creator_name
                    } 
                  })}
                  className="bg-gradient-to-r from-[#00FF94] to-[#00F0FF] hover:opacity-90 text-black font-bold rounded-full"
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  Learn About Affiliate Program
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Galaxy.ai post-checkout upsell — dismissible, shown above the
            Continue/Download row. Tracked with utm_campaign=buyer_postcheckout
            so we can measure conversion vs the buyer_purchase email funnel. */}
        <PostCheckoutUpsell />

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            onClick={() => navigate('/')}
            className="flex-1 bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-full py-6 text-lg font-bold"
          >
            Continue Shopping
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          {orders[0]?.download_url && (
            <Button
              onClick={() => window.open(orders[0].download_url, '_blank')}
              variant="outline"
              className="flex-1 border-white/10 hover:bg-white/5 rounded-full py-6 text-lg"
            >
              <Download className="w-5 h-5 mr-2" />
              Download Product
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CheckoutSuccess;
